import moment from 'moment'

type FactDeviationInput = {
	fact_dt?: number | null
	plan_time?: string | null
	has_deviation?: boolean
	has_weight_deviation?: boolean
}

const toPlanMoment = (
	date?: Date | string | null,
	planTime?: string | null,
) => {
	if (!date || !planTime) return null

	const [hours, minutes] = planTime.slice(0, 5).split(':').map(Number)
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return null

	return moment(date).set({
		hour: hours,
		minute: minutes,
		second: 0,
		millisecond: 0,
	})
}

const deviationMinutes = (deviation?: number | string | null) => {
	const value = Number(deviation)
	return Number.isNaN(value) ? 0 : value
}

export const isAfterFactTime = (factDt?: number | null) => {
	if (!factDt) return false
	return !moment(factDt).isAfter(moment())
}

export const isEventTimePending = (
	planTime?: string | null,
	date?: Date | string | null,
) => {
	const plan = toPlanMoment(date, planTime)
	return plan ? plan.isAfter(moment()) : false
}

export const isEventWindowClosed = (
	planTime?: string | null,
	date?: Date | string | null,
	deviation?: number | string | null,
) => {
	if (!date) return false
	if (moment(date).isBefore(moment(), 'day')) return true
	if (moment(date).isAfter(moment(), 'day')) return false

	const plan = toPlanMoment(date, planTime)
	if (!plan) return false

	return !moment().isBefore(
		plan.clone().add(deviationMinutes(deviation), 'minutes'),
	)
}

export const isEventMissed = (
	factDt?: number | null,
	date?: Date | string | null,
) => {
	if (factDt || !date) return false
	return moment(date).isBefore(moment(), 'day')
}

export const isFactTimeDeviation = (
	factDt?: number | null,
	planTime?: string | null,
	date?: Date | string | null,
	deviation?: number | string | null,
) => {
	if (!factDt || !planTime || !date) return false

	const plan = toPlanMoment(date, planTime)
	if (!plan) return false

	const fact = moment(factDt)
	const dayDiff = fact
		.clone()
		.startOf('day')
		.diff(plan.clone().startOf('day'), 'days')
	const diffMinutes = Math.abs(
		dayDiff * 24 * 60 +
			(fact.hours() * 60 + fact.minutes()) -
			(plan.hours() * 60 + plan.minutes()),
	)

	return diffMinutes > deviationMinutes(deviation)
}

export const isFactWeightDeviation = (
	fact: FactDeviationInput,
	date?: Date | string | null,
	deviation?: number | string | null,
	eventTime?: string | null,
) => {
	if (typeof fact.has_weight_deviation === 'boolean') {
		return fact.has_weight_deviation
	}
	if (!fact.fact_dt || !fact.has_deviation) return false

	return !isFactTimeDeviation(
		fact.fact_dt,
		fact.plan_time || eventTime,
		date,
		deviation,
	)
}

export const eventHasDeviation = (
	event: {
		time?: string
		deviation?: number | string
		feed_facts?: FactDeviationInput[]
	},
	date?: Date | string | null,
) =>
	(event.feed_facts ?? []).some(fact => {
		const timeDeviation = isFactTimeDeviation(
			fact.fact_dt,
			fact.plan_time || event.time,
			date,
			event.deviation,
		)
		const weightDeviation = isFactWeightDeviation(
			fact,
			date,
			event.deviation,
			event.time,
		)
		// Факт ровно на границе допуска бэк помечает has_deviation.
		// Плашка нужна только когда разница больше допустимой.
		const missedWithoutFact = fact.has_deviation === true && !fact.fact_dt

		return (
			timeDeviation ||
			weightDeviation ||
			isEventMissed(fact.fact_dt, date) ||
			missedWithoutFact
		)
	})
